import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { makeClaimClient, type ClaimAppError } from './client';
import { ClaimMobileView, type ClaimResponse } from './sections';

type State =
  | { status: 'loading' }
  | { status: 'expired' }
  | { status: 'error'; msg?: string }
  | { status: 'ok'; resp: ClaimResponse };

export default function ClaimMobilePage() {
  const { token = '' } = useParams();
  const client = useMemo(() => makeClaimClient(token), [token]);
  const [state, setState] = useState<State>({ status: 'loading' });

  const load = () => {
    setState({ status: 'loading' });
    client
      .get<ClaimResponse>('/claim')
      .then((resp) => setState({ status: 'ok', resp }))
      .catch((e: ClaimAppError) => {
        if (e.code === 'expired') setState({ status: 'expired' });
        else if (e.code === 'invalid' || e.status === 401) setState({ status: 'expired' });
        else if (e.status === 404) setState({ status: 'error', msg: 'No se encontró el reclamo.' });
        else setState({ status: 'error', msg: e.message || 'No se pudo cargar el reclamo.' });
      });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [token]);

  if (state.status === 'loading') return <CenterMsg emoji="⏳" title="Cargando reclamo…" />;
  if (state.status === 'expired')
    return (
      <CenterMsg emoji="🔒" title="Este enlace venció" sub="Pedí uno nuevo desde el sistema." />
    );
  if (state.status === 'error')
    return <CenterMsg emoji="⚠️" title="No se pudo abrir" sub={state.msg} action={load} />;

  return <ClaimMobileView resp={state.resp} client={client} onReload={load} />;
}

function CenterMsg({
  emoji,
  title,
  sub,
  action,
}: {
  emoji: string;
  title: string;
  sub?: string;
  action?: () => void;
}) {
  return (
    <div className="min-h-screen bg-gray-950 text-gray-100 flex flex-col items-center justify-center p-6 text-center">
      <div className="text-5xl mb-3">{emoji}</div>
      <div className="text-lg font-semibold">{title}</div>
      {sub && <div className="text-sm text-gray-400 mt-1">{sub}</div>}
      {action && (
        <button onClick={action} className="mt-4 px-4 py-2 rounded-lg bg-blue-600 text-white">
          Reintentar
        </button>
      )}
    </div>
  );
}
